import { api } from "@/redux/api";

import { RequestPaginationParamsWithUserId } from "@/types/request-params";

import {
	Email,
	EmailsResponse,
	EmailsResponseSchema,
	EmailSchema,
	SendTestSignatureRequest,
	SendTestSignatureResponse,
	SendTestSignatureResponseSchema,
} from "../../pages/Account/schema/email";
import { UnipileResponse, UnipileResponseSchema } from "../../pages/Account/schema/unipile";
import { CampaignAction } from "../../pages/Account/schema/pendingSchedules";

export const emailApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getEmails: builder.query<EmailsResponse, RequestPaginationParamsWithUserId>({
			query: ({ userId, sorting, ...params }) => ({
				url: `email/${userId}`,
				params: {
					...(params && { ...params }),
					...(sorting && { ...sorting }),
				},
				responseSchema: EmailsResponseSchema,
			}),
			providesTags: ["Email"],
		}),
		getEmail: builder.query<Email, number>({
			query: (id: number) => ({
				url: `email/${id}`,
				params: {
					id,
				},
				responseSchema: EmailSchema,
			}),
			providesTags: ["Email"],
		}),
		reconnectEmail: builder.mutation<UnipileResponse, { id: number; successRedirectUrl?: string }>({
			query: ({ id, successRedirectUrl }) => ({
				url: `email/reconnect/${id}`,
				method: "POST",
				body: { successRedirectUrl: successRedirectUrl || undefined },
				responseSchema: UnipileResponseSchema,
			}),
			invalidatesTags: ["Email"],
		}),
		updateEmail: builder.mutation<
			Partial<Email>,
			Partial<Email> & { saveForAllOtherEmailsOfThisUser?: boolean }
		>({
			query: ({ id, ...body }) => ({
				url: `email/${id}`,
				method: "PATCH",
				body,
				responseSchema: EmailSchema,
			}),
			invalidatesTags: ["Email", "Me"],
		}),
		deleteEmail: builder.mutation<
			Partial<Email>,
			{ id: number; campaignActions?: CampaignAction[] }
		>({
			query: ({ id, campaignActions }) => ({
				url: `email/${id}`,
				method: "DELETE",
				body: campaignActions ? { campaignActions } : undefined,
				responseSchema: EmailSchema,
			}),
			invalidatesTags: ["Email", "Me", "Users"],
		}),
		sendTestSignature: builder.mutation<SendTestSignatureResponse, SendTestSignatureRequest>({
			query: (body) => ({
				url: `email/test-signature`,
				method: "POST",
				body,
				responseSchema: SendTestSignatureResponseSchema,
			}),
		}),
	}),
});

export const {
	useGetEmailQuery,
	useGetEmailsQuery,
	useLazyGetEmailQuery,
	useUpdateEmailMutation,
	useDeleteEmailMutation,
	useReconnectEmailMutation,
	useSendTestSignatureMutation,
} = emailApi;
export const emailApiReducer = emailApi.reducer;
export const emailApiMiddleware = emailApi.middleware;
